import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldErrors, FieldPath, UseFormReturn } from 'react-hook-form';
import {
  Modal,
  Form,
  FormField,
  Button,
  WizardStepper,
  FORM_FIELD_MESSAGE,
  type FieldOption,
} from '@noor/ui';
import { getApiErrorMessage, getApiFieldErrors, isApiError } from '@noor/api';
import {
  confirmDialog,
  toast,
  useFormSeed,
  MAX_IMAGE_LABEL,
  type ModalRenderProps,
} from '@noor/lib';
import { useMenuProduct, type MenuProduct } from '@/entities/menu';
import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  type ProductFormImage,
  type ProductFormValues,
} from '../model/schema';
import { useUpsertProduct } from '../model/use-upsert-product';
import { useDeleteProduct } from '../model/use-delete-product';
import { useUploadProductImages } from '../model/use-upload-images';
import { buildDefaults, toProductInput } from '../lib/product-form';
import { FiscalFields } from './fiscal-fields';
import { SellingHoursField } from './selling-hours-field';
import { ModifierStep } from './modifier-step';
import { RecommendationsSection } from './recommendations-section';
import { CompositionNutritionFields } from './composition-nutrition-fields';

/** White bordered field pill — overrides the design system's filled default. */
const FIELD =
  'rounded-3xl border-input bg-card shadow-xs focus-visible:border-input-border-focus';

/** Gray filled pill button: muted bg, no border. */
const GRAY_PILL =
  'h-12 min-w-[11rem] rounded-full border-transparent bg-muted text-base font-semibold text-foreground shadow-none hover:bg-muted/70';

/**
 * Label→control gap, tightened from the design system's default 8px to 6px.
 *
 * `relative pb-5` is what anchors the validation message (see {@link MESSAGE}):
 * the padding reserves the message's line on EVERY field, errored or not, so a
 * row of side-by-side fields keeps one shared height. It is `pb-5` (20px) — one
 * `text-sm` line — rather than the 24px it used to be, which left the message
 * floating a visible gap below the input it belonged to.
 */
const FIELD_GROUP = `relative gap-1.5 pb-6`;

/**
 * Validation text, taken OUT OF FLOW into the space {@link FIELD_GROUP} reserves.
 *
 * A `FormField` is a grid, so an in-flow message is a grid ROW: an errored
 * field grew taller than its clean siblings, which shifted every field below it
 * and left side-by-side controls unequal. Absolute positioning means an error
 * can never change a field's height.
 *
 * Anchored to `bottom-0`, INSIDE the field's own padded box — not `top-full`,
 * which starts after the padding and hung the message a full line BELOW the
 * field. Inside a modal whose body is `overflow-y-auto` that overhang is
 * clipped, which is how the date fields came to turn red while showing no
 * message at all: the text was in the DOM and announced to screen readers, and
 * simply painted outside the scroll region.
 *
 * `pointer-events-none` so the text cannot swallow clicks meant for whatever
 * sits below it.
 */
const MESSAGE = `pointer-events-none absolute bottom-0 left-0 ${FORM_FIELD_MESSAGE}`;

/** A grouping card on the scrim — the design's «Добавление продукта» panels. */
const CARD = 'flex flex-col gap-5 rounded-3xl bg-muted p-5';

/** Card heading — 18px semibold («Text/18/Semibold»), shared by every step. */
const CARD_TITLE = 'text-lg font-semibold tracking-tight text-foreground';

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', lang: 'ru' },
  { suffix: 'Uz', lang: 'uz' },
  { suffix: 'En', lang: 'en' },
] as const;

/**
 * Which fields each wizard step owns, so "Продолжить" can validate just that
 * step. Step 3's rows are addressed by prefix (`modifiers`) because their paths
 * are dynamic — RHF validates the whole array from the root name.
 */
const STEP_FIELDS: FieldPath<ProductFormValues>[][] = [
  [
    'nameRu',
    'nameUz',
    'nameEn',
    'descriptionRu',
    'descriptionUz',
    'descriptionEn',
    'compositionRu',
    'compositionUz',
    'compositionEn',
    'calories',
    'fats',
    'proteins',
    'categoryId',
    'alwaysReady',
    'isAdult',
    'prepTimeMinutes',
    'availableFrom',
    'availableTo',
  ],
  [
    'images',
    'ikpuCode',
    'unitCode',
    'measureAmount',
    'measurementType',
    'vatPercent',
    'hasMarkedCode',
    'markedCode',
  ],
  ['modifiersDisabled', 'modifiers'],
];

/**
 * Field label: text, a required marker, and (for localized rows) the language
 * it captures. Localized name/description rows label by language name alone
 * (Figma 2087329211: «Русский»/«Узбекский»/«Английский»), so `children` carries
 * the language and no `tag` is rendered.
 */
function FieldLabel({
  children,
  required = false,
}: {
  children: string;
  required?: boolean;
}) {
  return (
    <FormField.Label className="flex items-center gap-px px-1 text-sm font-medium tracking-tight text-foreground">
      {children}
      {required && <span className="text-primary">*</span>}
    </FormField.Label>
  );
}

type ProductModalProps =
  | ModalRenderProps<'add-product'>
  | ModalRenderProps<'edit-product'>;

/**
 * Add / edit product wizard (Figma 838-14553 → 840-18449), registered under
 * `add-product` / `edit-product`.
 *
 * Three steps — details, photos, modifiers — over ONE form. The steps are a
 * presentation split, not a state split: a single `useZodForm` holds every
 * field, so the by-id re-seed (`useFormSeed`) resets the whole record at once.
 * Per-step forms would let a read landing while the merchant is on step 3
 * silently reset steps 1–2.
 *
 * The design's "Выбрать существующий" modifier branch (frame 840-18449) is not
 * built: it needs a modifier-group library the merchant API does not expose.
 * See `ModifierGroupCard`.
 */
export function ProductModalDesktop({
  payload,
  close,
  form,
}: ProductModalProps & { form: UseFormReturn<ProductFormValues> }) {
  const { t } = useTranslation('eats');
  const upsert = useUpsertProduct();
  const remove = useDeleteProduct();
  const { uploadImages, isUploading } = useUploadProductImages();
  const [step, setStep] = useState(0);

  const isEditing = payload.mode === 'edit';
  const product = payload.mode === 'edit' ? payload.product : undefined;
  const { categories, defaultCategoryId } = payload;

  const categoryOptions = useMemo<FieldOption[]>(
    () => categories.map((c) => ({ label: c.name, value: String(c.id) })),
    [categories],
  );

  // The catalog card is a projection — it omits the photo gallery and carries a
  // branch-resolved price — so an edit re-reads the product itself.
  const { data: fetched, dataUpdatedAt } = useMenuProduct(product?.id);

  // The fetched record wins field by field, with the card as the fallback so
  // the form is populated immediately and simply sharpens when the read lands.
  const source = useMemo<MenuProduct | undefined>(
    () => (product ? { ...product, ...fetched } : undefined),
    [product, fetched],
  );

  // `onSubmit`, not the shared `onTouched` default: this is a THREE-STEP
  // wizard, so merely tabbing through step 1 on the way to step 2 marked every
  // field touched and painted required-errors under inputs the merchant had not
  // finished with yet. Validation now happens where the merchant asks for it —
  // «Продолжить» (which triggers just that step's fields) and the final submit —
  // and `reValidateMode` then keeps a corrected field's error clearing as they
  // type, rather than waiting for another submit.
  // Re-seed when the by-id read delivers a NEWER record — that is what fills in
  // the gallery, the translations, the modifier groups and the authoritative
  // price. Keyed on `dataUpdatedAt`, which only advances when a fetch resolves,
  // so it cannot reset the form mid-typing.
  const { reset } = form;
  useFormSeed(
    true,
    product?.id === undefined ? undefined : String(product.id),
    dataUpdatedAt,
    () => {
      // The shared form may already contain edits made in the other responsive
      // view. A newly mounted view must not reseed those values.
      if (!form.formState.isDirty) reset(buildDefaults(source, defaultCategoryId));
    },
  );

  const alwaysReady = form.watch('alwaysReady') ?? false;

  // Ticking "always ready" retires the prep-time answer: drop the value AND any
  // validation error it left behind, so a previously-empty required field can't
  // keep the form unsubmittable while its input is no longer even rendered.
  useEffect(() => {
    if (!alwaysReady) return;
    form.setValue('prepTimeMinutes', undefined);
    form.clearErrors('prepTimeMinutes');
  }, [alwaysReady, form]);

  /**
   * Advance only if THIS step's fields validate.
   *
   * Without the per-step gate the merchant reaches the last step and the submit
   * fails on a field two screens back, with the message rendered somewhere they
   * cannot see. Validating on the way forward puts the error next to the input
   * that caused it.
   */
  async function handleNext(): Promise<void> {
    const fields = STEP_FIELDS[step];
    if (fields && !(await form.trigger(fields))) return;
    setStep((current) => Math.min(current + 1, STEP_FIELDS.length - 1));
  }

  function handleInvalid(errors: FieldErrors<ProductFormValues>): void {
    // Diagnostic: log the failing paths so a "dead" save can be traced.
    console.error('[product-modal] save blocked, validation errors:', errors);
    const errorRoots = new Set(Object.keys(errors));
    const invalidStep = STEP_FIELDS.findIndex((fields) =>
      fields.some((field) => errorRoots.has(String(field).split('.')[0] ?? field)),
    );
    if (invalidStep >= 0) setStep(invalidStep);
  }

  /**
   * Freshly picked photos still hold `blob:` preview URLs, so they are uploaded
   * to object storage first and the returned storage KEYS are what get saved.
   *
   * A failed upload does NOT abort the save. Object storage is a separate host
   * from the API and has gone down on its own, so aborting meant a photo problem
   * silently blocked edits to name, price and description that never involved a
   * photo. The product is saved WITHOUT the new photos and the failure is
   * reported on its own.
   */
  async function handleSubmit(values: ProductFormValues): Promise<void> {
    let images: ProductFormImage[];
    let uploadFailed = false;
    let uploadError: unknown;
    try {
      images = await uploadImages(values.images);
    } catch (error) {
      uploadFailed = true;
      uploadError = error;
      // Fall back to the entries that need no upload. A freshly-picked photo
      // carries a `blob:` URL that would be persisted verbatim and 404 on the
      // next load, so those are dropped; already-stored ones survive untouched.
      images = (values.images ?? []).filter((image) => !image.file);
    }

    // Whether the product had photos before this edit — the only way to tell an
    // untouched gallery ("leave them") from one the merchant emptied ("clear").
    const savedImageCount = source?.imageUrls?.length ?? (source?.imageUrl ? 1 : 0);
    const hadSavedImages = savedImageCount > 0;
    const input = toProductInput(
      values,
      images,
      hadSavedImages,
      savedImageCount,
      // WHICH photo was the cover, by its gallery id — so a submit that merely
      // re-stars the same one is recognized as no change and touches no image
      // endpoint, while starring a different one is always detected. An index
      // cannot do this: the grid promotes by swapping into slot 0, so the
      // starred entry's index is always 0.
      source?.imageIds?.[source.coverIndex ?? 0],
      source?.modifiersDisabled ?? false,
      { from: source?.availableFrom, to: source?.availableTo },
      source,
    );

    // The editor does not choose branches. Nothing on the wire reports which
    // branches stock a product, so this is `undefined` on an edit and the
    // update touches only the overlay of the branch being viewed — see
    // `menuApi.updateProduct`.
    const existingBranchIds = source?.branchIds;

    upsert.mutate(
      product === undefined
        ? input
        : {
            ...input,
            id: product.id,
            ...(existingBranchIds === undefined ? {} : { branchIds: existingBranchIds }),
          },
      {
        onSuccess: (outcome) => {
          // Every successful save is confirmed. The catalog only shows the
          // change once its refetch lands, so closing in silence read as
          // ambiguous — the merchant had no way to tell a save from a dismiss.
          //
          // A gated merchant has their edit QUEUED for review rather than
          // applied, which is still a success but a DIFFERENT one: the catalog
          // will keep showing the old values until a moderator approves, so
          // that case says so instead of claiming the save landed.
          toast.success(
            outcome.status === 'pending'
              ? t('menu.product.form.pendingReview')
              : isEditing
                ? t('menu.product.form.successEdit')
                : t('menu.product.form.success'),
          );
          // The product saved but its new photos did not. Said plainly, and
          // AFTER the save, so it reads as "everything else went through".
          if (uploadFailed) {
            toast.error(
              getApiErrorMessage(uploadError, t('menu.product.form.uploadErrorSaved')),
            );
          }
          close();
        },
        // Surface the server's OWN reason rather than a bare "не удалось".
        // A rejected product write is almost always a specific, actionable
        // validation failure (a fiscal code the OFD classifier refuses, a
        // branch overlay the merchant no longer owns, a moderation rule), and
        // collapsing every one of them into the same sentence left the
        // merchant — and us — with nothing to act on but a red banner.
        onError: (error) => {
          const { reason } = getApiFieldErrors(error);
          const fallback =
            reason ??
            (isApiError(error) && error.message?.trim()
              ? error.message
              : t('menu.product.form.error'));
          toast.error(getApiErrorMessage(error, fallback));
        },
      },
    );
  }

  /**
   * Delete the product being edited, behind a confirmation.
   *
   * `DELETE /products/{id}` is a SOFT delete of the merchant-owned product — it
   * retires the product in EVERY branch, not just the one on screen. That is a
   * different operation from unchecking branches in this same form, so the
   * confirmation spells out the scope.
   */
  async function handleDelete(): Promise<void> {
    if (product === undefined) return;

    await confirmDialog({
      title: t('menu.product.deleteConfirm.title'),
      description: t('menu.product.deleteConfirm.description', { name: product.name }),
      confirmLabel: t('menu.product.deleteConfirm.confirm'),
      cancelLabel: t('menu.product.deleteConfirm.cancel'),
      destructive: true,
      onConfirm: () =>
        remove.mutateAsync(product.id, {
          onSuccess: () => {
            toast.success(t('menu.product.deleteSuccess'));
            close();
          },
          onError: (error) =>
            toast.error(getApiErrorMessage(error, t('menu.product.deleteError'))),
        }),
    });
  }

  const isLastStep = step === STEP_FIELDS.length - 1;

  return (
    <Modal
      title={t(isEditing ? 'common:modal.edition' : 'common:modal.creation')}
      /* `max-h` + `grid-rows-[auto_minmax(0,1fr)]` are what make the body
       * scroll instead of the modal growing. The base `DialogContent` sets no
       * height cap and lays its children out as a GRID, whose rows size to
       * content by default — so a tall step (several modifier groups) pushed
       * the modal past the viewport and the body's `overflow-y-auto` could
       * never engage, having no constrained height to overflow. Capping the
       * dialog and pinning the second row to `minmax(0, 1fr)` gives the form a
       * bounded box to scroll inside. */
      className={
        'flex max-h-[calc(100dvh-2rem)] max-w-[70rem] flex-col gap-0 ' +
        'overflow-hidden rounded-[2rem] p-5 ' +
        'sm:max-h-[calc(100dvh-4rem)] sm:max-w-[70rem] sm:p-8 ' +
        // The close button follows the padding in, or it sits half off a
        // full-bleed phone sheet; the header reserves a gutter so a long title
        // ("Изменить продукт") never runs underneath it.
        '[&>button]:top-5 [&>button]:right-5 sm:[&>button]:top-8 sm:[&>button]:right-8 ' +
        '[&>[data-slot=dialog-header]]:pr-12'
      }
    >
      {/* `min-h-0` on both this and the scroll region below: a flex child's
       * default `min-height:auto` refuses to shrink below its content, which
       * silently defeats every ancestor's overflow. */}
      <Form
        form={form}
        onSubmit={handleSubmit}
        onInvalid={handleInvalid}
        className="flex min-h-0 flex-1 flex-col gap-6 pt-6"
      >
        <WizardStepper
          className="shrink-0"
          steps={[
            t('menu.product.form.steps.details'),
            t('menu.product.form.steps.photos'),
            t('menu.product.form.steps.modifiers'),
          ]}
          current={step}
          // Editing an existing product means every step already holds a valid
          // record, so all three are reachable at once — the merchant who opened
          // the wizard to change a modifier should not have to walk the whole
          // form to reach it. A CREATE still unlocks step by step, so
          // «Продолжить» keeps validating the step being left.
          allStepsReachable={isEditing}
          onStepSelect={setStep}
        />

        {/* Every step stays MOUNTED and is hidden with `hidden` rather than
         * unmounted. Unmounting drops the inputs' registration, so RHF discards
         * those values — a merchant who filled step 1, moved on, and came back
         * would find it blank, and a submit from step 3 would send nothing. */}
        {/* `-mx-1 px-1`: a focus ring is a 3px box-shadow drawn OUTSIDE the
         * field's border box, and `overflow-y-auto` forces the CROSS axis to
         * clip too — so without room to render into, the leftmost field's ring
         * was cut off flat. The negative margin keeps the cards flush with the
         * dialog's gutter. `pr-1` (kept, as the right half of `px-1`) also
         * holds the scrollbar off the fields. */}
        <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-1">
          <div className={step === 0 ? 'flex flex-col gap-6' : 'hidden'}>
            <div className={CARD}>
              <span className={CARD_TITLE}>{t('menu.product.form.nameSection')}</span>
              <div className="flex flex-col gap-5 sm:flex-row">
                {LOCALES.map(({ suffix, lang }) => (
                  <FormField
                    key={suffix}
                    name={`name${suffix}`}
                    className={`flex-1 ${FIELD_GROUP}`}
                  >
                    <FieldLabel required>{t(`utilities.languages.${lang}`)}</FieldLabel>
                    {/* `maxLength` stops the overrun at the keystroke, so the
                     * cap is felt rather than reported; the schema's `.max()`
                     * still guards a paste the browser lets through. */}
                    <FormField.Input
                      className={FIELD}
                      maxLength={NAME_MAX_LENGTH}
                      placeholder={t(`menu.product.form.namePlaceholder${suffix}`)}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>
                ))}
              </div>
            </div>

            <div className={CARD}>
              <span className={CARD_TITLE}>
                {t('menu.product.form.descriptionSection')}
              </span>
              <div className="flex flex-col gap-5 sm:flex-row">
                {LOCALES.map(({ suffix, lang }) => (
                  <FormField
                    key={suffix}
                    name={`description${suffix}`}
                    className={`flex-1 ${FIELD_GROUP}`}
                  >
                    <FieldLabel required>{t(`utilities.languages.${lang}`)}</FieldLabel>
                    <FormField.TextArea
                      className={`min-h-24 ${FIELD}`}
                      maxLength={DESCRIPTION_MAX_LENGTH}
                      placeholder={t(`menu.product.form.descriptionPlaceholder${suffix}`)}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>
                ))}
              </div>
            </div>

            <div className={CARD}>
              <CompositionNutritionFields
                fieldClassName={FIELD}
                messageClassName={MESSAGE}
              />
            </div>

            <div className={CARD}>
              <span className={CARD_TITLE}>{t('menu.product.form.detailsSection')}</span>
              <div className="flex flex-col gap-5 sm:flex-row">
                {/* No branch picker. Which branches stock a product is a
                 * per-branch overlay (`RestaurantProduct`), managed from the
                 * branch's own menu rather than from the product editor —
                 * same reasoning as price above. Create no longer sends
                 * overlays in the product POST body; editing leaves the
                 * existing set untouched. */}

                <FormField name="categoryId" className={`flex-1 ${FIELD_GROUP}`}>
                  <FieldLabel>{t('menu.product.form.categoryLabel')}</FieldLabel>
                  <FormField.Select
                    className={FIELD}
                    options={categoryOptions}
                    placeholder={t('menu.product.form.categoryPlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                {/* Price, in so'm. It is a per-BRANCH overlay
                 * (`RestaurantProductInput.price`, optional on the wire) rather
                 * than a product property, so this box sets it for the branch
                 * the editor is scoped to — the one whose catalog the merchant
                 * opened. It stays OPTIONAL for that reason: it was required
                 * once, which blocked saving a name or description fix on any
                 * product whose branch price had not been set yet. An empty box
                 * is omitted from the write, which the API reads as "leave the
                 * stored price alone". */}
                <FormField name="price" className={`flex-1 ${FIELD_GROUP}`}>
                  <FieldLabel>{t('menu.product.form.priceLabel')}</FieldLabel>
                  <FormField.Price
                    className={FIELD}
                    placeholder={t('menu.product.form.pricePlaceholder')}
                    suffix={t('menu.product.form.currencySuffix')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>
              </div>

              {/* "Always ready" and prep time answer the same question — how
               * long the kitchen needs — so they are one control group. Ticking
               * the box removes the prep-time input entirely (rather than
               * disabling it) because an always-ready product has no wait. */}
              <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
                {/* Both toggles stack in the left column, under the category
                 * select, so the prep-time field keeps the price's column. */}
                <div className="flex flex-col gap-3 sm:flex-1">
                  <FormField name="isAdult" className="py-1">
                    <FormField.Switch label={t('menu.product.form.isAdult')} />
                  </FormField>
                  <FormField name="alwaysReady" className="py-1">
                    <FormField.Checkbox
                      label={t('menu.product.form.alwaysReady')}
                      className="[&_label]:text-sm [&_label]:font-medium [&_label]:tracking-tight [&_label]:text-foreground"
                      checkboxClassName="size-6 rounded-lg border-2"
                    />
                  </FormField>
                </div>

                {alwaysReady ? (
                  <p className="text-xs leading-relaxed text-muted-foreground sm:flex-1">
                    {t('menu.product.form.alwaysReadyHint')}
                  </p>
                ) : (
                  <FormField
                    name="prepTimeMinutes"
                    className={`sm:flex-1 ${FIELD_GROUP}`}
                  >
                    <FieldLabel required>
                      {t('menu.product.form.prepTimeLabel')}
                    </FieldLabel>
                    {/* `Number`, not a bare `type="number"`: the native input's
                        `min`/`max` only gate the stepper arrows, so a prep time
                        of 4845 minutes could be TYPED and was only refused on
                        submit. This clamps to the 1..120 the schema accepts
                        (max mirrors the API's cook_time_seconds cap of 7200s)
                        and keeps the field whole-minutes-only. */}
                    <FormField.Number
                      className={FIELD}
                      min={1}
                      max={120}
                      placeholder={t('menu.product.form.prepTimePlaceholder')}
                      suffix={t('menu.product.form.prepTimeSuffix')}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>
                )}
              </div>

              <SellingHoursField className="flex flex-col gap-2" fieldClassName={FIELD} />
            </div>
          </div>

          <div className={step === 1 ? 'flex flex-col gap-5 lg:flex-row' : 'hidden'}>
            <div className={`flex-1 ${CARD}`}>
              {/* 12px title→hint, per Figma 838:16555. */}
              <div className="flex flex-col gap-3">
                <span className={CARD_TITLE}>{t('menu.product.form.photoSection')}</span>
                <p className="text-xs tracking-tight whitespace-pre-line text-muted-foreground">
                  {t('menu.product.form.photoHint')}
                </p>
              </div>
              <FormField name="images">
                {/* The slots carry their own image-add glyph, so the label is
                 * the bare prompt — no icon of its own. */}
                <FormField.ImageGrid
                  uploadLabel={t('menu.product.form.uploadPhoto')}
                  removeLabel={t('menu.product.form.removePhoto')}
                  primaryLabel={t('menu.product.form.coverPhoto')}
                  // Oversized picks are named rather than dropped in silence.
                  // The grid stays presentational and hands them up here.
                  onRejected={(names) =>
                    toast.error(
                      t('menu.product.form.photoTooLarge', {
                        name: names.join(', '),
                        limit: MAX_IMAGE_LABEL,
                      }),
                    )
                  }
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
            </div>

            <div className={`flex-1 ${CARD}`}>
              <span className={CARD_TITLE}>{t('menu.product.form.fiscalSection')}</span>
              {/* Fiscal classifiers — see `FiscalFields` for why they collapse. */}
              <FiscalFields />
            </div>
          </div>

          <div className={step === 2 ? 'block' : 'hidden'}>
            <ModifierStep />
            {isEditing && product ? (
              <RecommendationsSection productId={String(product.id)} />
            ) : null}
          </div>
        </div>

        <Form.RootError />

        {/* Footer: back once past the first step, then continue or save. */}
        <div className="flex shrink-0 items-center justify-end gap-3 pt-2">
          {isEditing && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={remove.isPending}
              className="mr-auto text-base font-semibold tracking-tight text-destructive disabled:opacity-60"
            >
              {t('menu.product.form.deleteProduct')}
            </button>
          )}

          {step > 0 && (
            <Button
              type="button"
              className={GRAY_PILL}
              onClick={() => setStep((current) => current - 1)}
            >
              {t('common:actions.back')}
            </Button>
          )}

          {isLastStep ? (
            /* The two footer buttons MUST carry distinct keys. They sit in the
             * same JSX slot and differ only by props, so without them React
             * reconciles the pair as one element and merely mutates `type` on
             * the LIVE node. The click that advances 2 → 3 then re-renders
             * synchronously, flipping the very button being clicked to
             * `type="submit"` before the browser resolves the click's default
             * action — which submits the form and saved the product the moment
             * the merchant reached the last step. Separate keys mount a fresh
             * node instead, so the clicked one stays `type="button"`. */
            <Button
              key="submit"
              type="submit"
              size="lg"
              className="h-12 min-w-[11rem] rounded-full text-base"
              disabled={upsert.isPending || isUploading}
            >
              {isUploading ? t('menu.product.form.uploading') : t('common:actions.save')}
            </Button>
          ) : (
            <Button
              key="continue"
              type="button"
              size="lg"
              className="h-12 min-w-[11rem] rounded-full text-base"
              onClick={() => void handleNext()}
            >
              {t('common:actions.next')}
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
}
